import { FormNavigationItem } from 'app/modules/forms/components/navigation';
import { Component, EventEmitter, HostListener, inject, Input, OnInit, Output, signal, ViewEncapsulation } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { fuseAnimations } from "@fuse/animations";
import { AlertService } from "@full-fledged/alerts";
import _ from "lodash";
import { FormNavigationService } from "../form-navigation.service";
import { GroupControl } from "app/shared/model";
import { SubmitApplicationResponse } from "app/shared/services/model/application-service.model";
import { ValidationService } from 'app/modules/forms/application-editor/validation';
import { FormService } from 'app/modules/forms/form.service';
import { hasValue } from 'app/shared';

@Component({
    selector    : 'fullscreen-navigation',
    templateUrl : './fullscreen-navigation.component.html',
    styleUrl    : './fullscreen-navigation.component.scss',
    encapsulation: ViewEncapsulation.None,
    animations  : fuseAnimations,
    imports     : [
        MatButtonModule,
        MatIconModule,
    ],
})
export class FullscreenNavigationComponent implements OnInit
{
    @Input() controlGroups: GroupControl[] = [];
    @Input() currentGroupIndex: number;
    @Output() onNavigate = new EventEmitter<number>();

    private readonly _alertService = inject(AlertService);
    protected readonly _navigationService = inject(FormNavigationService);
    private readonly _validationService = inject(ValidationService);
    private readonly _formService = inject(FormService);

    currentMenuItem = signal<FormNavigationItem>(null); // this._navigationService.currentMenuItem$;
    currentParentMenuItem = signal<FormNavigationItem>(null);

    backBtnTitle: string = 'Back';
    okBtnTitle: string = 'Ok';
    submitBtnTitle = 'Submit';

    canSubmit = signal<boolean>(false);
    canNavigateForward = signal<boolean>(false);
    canNavigateBackward = signal<boolean>(false);

    get isBusy(): boolean {
        return this._navigationService.isBusy?.() ?? false;
    }

    private currentTabIndex: number = 0;

    constructor() {
        this._navigationService.currentMenuItem$
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: (item: FormNavigationItem) => {
                    this.currentMenuItem.set(item);
                    if (!item.controlId) {
                        this.currentParentMenuItem.set(item);
                    } else {
                        let parent = this._navigationService.findMenuItemById(item.pageId);
                        if (parent != item) {
                            this.currentParentMenuItem.set(parent);
                        }
                    }

                    this.updateNavigationState();
                },
                error: console.error
            });

    }

    @HostListener('document:keyup.enter', ['$event'])
    onEnterKey(event) {
        // console.log('ENTER KEY PRESSED!!', event);
        if (event.target?.nodeName == 'INPUT') {
            this.nextSection();
        }
    }

    ngOnInit(): void {
        this.currentTabIndex = this.currentGroupIndex;
        this.updateNavigationState();
    }

    updateNavigationState() {
        let groupId = this.currentMenuItem().controlId;
        const groupIndex = this.controlGroups.findIndex((group: GroupControl, index: number) => {
            return group.id == groupId;
        });

        if (groupIndex >= 0) {
            this.currentTabIndex = groupIndex;
        }

        this.updateNavigationControls();
    }

    updateNavigationControls() {
        const nextGroupIndex = _.findIndex(this.controlGroups, (group: GroupControl, index: number) => {
            return index > this.currentTabIndex && group.lastIsVisibleValue;
        }, this.currentTabIndex);

        const previousGroupIndex = _.findLastIndex(this.controlGroups, (group: GroupControl, index: number) => {
            return index < this.currentTabIndex && group.lastIsVisibleValue;
        }, this.currentTabIndex);

        this.canSubmit.set(nextGroupIndex == -1 && this._navigationService.canSubmit());
        this.canNavigateForward.set(nextGroupIndex >= 0 || this._navigationService.canNavigateForward());
        this.canNavigateBackward.set(previousGroupIndex >= 0 || this._navigationService.canNavigateBackward());

        this.updatePageProgress();

        this.onNavigate.emit(this.currentTabIndex);
    }

    nextSection(currentGroupControl?: GroupControl) {
        if (this.isBusy) {
            return;
        }
        // const currentGroup = this.controlGroups[this.currentTabIndex];
        // Validate all controls in current group before allowing page change.
        if (!this.validateControlGroup()) {
            return;
        }

        // use navigation service
        this._navigationService.navigateForward();
        this.updateNavigationControls();
    }

    previousSection() {
        if (this.isBusy) {
            return;
        }
        // use navigation service
        this._navigationService.navigateBackward();
        this.updateNavigationControls();
    }

    nextPage() {
        if (this.isBusy) {
            return;
        }
        this._navigationService.navigateForward();
    }

    previousPage() {
        if (this.isBusy) {
            return;
        }
        this._navigationService.navigateBackward();
    }

    submitApplication() {
        if (this.isBusy) {
            return;
        }
        this._navigationService.submitApplication()
             .subscribe({
                 next: (result: SubmitApplicationResponse) =>
                 {
                     // TODO: Alert messages in language pack
                     if (result.success && result.valid) {
                         this._alertService.success('Application submitted successfully.');
                     } else {
                         console.log('Nav Menu - Submit Errors:', result.errors);
                         this._alertService.danger(this.hasValidationFailure(result)
                                ? 'Please correct the validation errors present on this page'
                                : 'Application could not be submitted.');
                     }
                 },
                 error: this._alertService.danger,
                 complete: console.info
             });
    }

    private hasValidationFailure(result: SubmitApplicationResponse): boolean {
        return result.valid === false && (hasValue(result.errors) || result.firstPage?.pageId != null);
    }

    private updatePageProgress() {
        this.currentParentMenuItem()?.progress.setStep(this.currentTabIndex);
        // this._navigationService.setProgress(this.currentTabIndex + 1, this.controlGroups.length);
    }

    private validateControlGroup(): boolean {
        this._validationService.clearErrors();
        const currentGroup = this.controlGroups[this.currentTabIndex];

        // const errors = this._validationService.validateAll(
        //     [ currentGroup ],
        //     this._formService.applicationDataSnapshot,
        //     null,
        // ) || {};

        const result = this._validationService.validateApplicationLocal(
            [ currentGroup ],
            this._formService.applicationDataSnapshot,
            null,               // pageId
            currentGroup.id,    // subPageId
            null                // index
        );

        return result.success && result.valid;
        // return (
        //     Object.keys(errors).filter((key) => {
        //         // key.startsWith(qualifier.safeControlName('')),
        //         return key;
        //     }).length == 0
        // );
    }
}
